import { z } from 'zod';
import { BoardTypeSchema } from './Board.js';

// =====================================================================
// Legacy: discovered-device announcement event (Plan 8 / earlier work)
//
// Phase 13B-RHIA R2: extended with two optional fields that mirror the
// WS `EventPayload::Discovery` variant. Both default-absent so older
// firmwares that omit them parse cleanly.
//   - parent_can_id   — drive board CAN ID for an accessory chip
//   - firmware_version — already required here; left as-is for back-compat
// =====================================================================

export const DiscoveredDeviceEventSchema = z.object({
  uuid: z.string().min(1),
  board_type: BoardTypeSchema,
  suggested_node_id: z.number().int().min(0).max(255),
  firmware_version: z.string().min(1),
  parent_can_id: z.number().int().min(0).max(255).nullable().optional(),
});

export type ParsedDiscoveredDeviceEvent = z.infer<typeof DiscoveredDeviceEventSchema>;

// =====================================================================
// Phase 13B-RHIA R2 — WS EventPayload::Discovery
//
// Mirrors the `{ kind: "discovery", ... }` variant of EventPayload in
// `crates/rhia-protocol/src/ws_envelope.rs`. The two new optional fields
// are emitted by per-accessory discovery on PLCslayer V2 drive boards;
// legacy (CAN-bus) Discovery events omit them entirely.
//
// ts-rs renders Rust `Option<T>` with `skip_serializing_if = "Option::is_none"`
// as `T | null` in the .d.ts, so the schema accepts both null and absent.
// =====================================================================

// Phase B (2026-05-30): per-axis TMC5160 capabilities. Emitted by
// PLCslayer V2 drive boards as one Discovery frame per axis with
// board_type == "stepper_motor". `chopper_mode`: 0=Spread, 1=Stealth,
// 2=StallGuard, 3=DCStep. `ref_stop_flags` bits: stop_l(1) | stop_r(2)
// | pol_l(4) | pol_r(8) | soft(16). `global_scaler` 0 ≡ 256 (gain 1.0).
export const MotorCapabilitiesSchema = z.object({
  chopper_mode: z.number().int().min(0).max(3),
  current_ma: z.number().int().min(0).max(0xffff_ffff),
  ref_stop_flags: z.number().int().min(0).max(255),
  shaft_invert: z.boolean(),
  tpowerdown: z.number().int().min(0).max(255),
  thigh: z.number().int().min(0).max(0xffff_ffff),
  global_scaler: z.number().int().min(0).max(255),
});

export type ParsedMotorCapabilities = z.infer<typeof MotorCapabilitiesSchema>;

export const EventPayloadDiscoverySchema = z.object({
  kind: z.literal('discovery'),
  uuid: z.string().min(1),
  board_type: BoardTypeSchema,
  suggested_can_id: z.number().int().min(0).max(255),
  parent_can_id: z.number().int().min(0).max(255).nullable().optional(),
  firmware_version: z.string().nullable().optional(),
  // Phase B: only populated when board_type === "stepper_motor". Older
  // firmwares and non-stepper boards omit the field entirely.
  motor_capabilities: MotorCapabilitiesSchema.nullable().optional(),
});

export type ParsedEventPayloadDiscovery = z.infer<typeof EventPayloadDiscoverySchema>;

// Phase C (2026-05-30): live per-axis telemetry. Emitted at ~200 ms
// while an axis is running; idle motors stay quiet. `drv_status`
// carries SG_RESULT in bits 9:0 alongside all fault bits.
export const EventPayloadMotorTelemetrySchema = z.object({
  kind: z.literal('motor_telemetry'),
  can_id: z.number().int().min(0).max(255),
  axis: z.number().int().min(0).max(7),
  xactual: z.number().int(),
  vactual: z.number().int(),
  drv_status: z.number().int().min(0).max(0xffff_ffff),
});

export type ParsedEventPayloadMotorTelemetry = z.infer<typeof EventPayloadMotorTelemetrySchema>;

// Phase C: rising-edge fault notification. Emitted at most once per
// axis per 1 s while the fault persists; bits that clear themselves
// do not re-emit. `gstat` holds reset/drv_err/uv_cp in bits 0..2.
export const EventPayloadMotorFaultSchema = z.object({
  kind: z.literal('motor_fault'),
  can_id: z.number().int().min(0).max(255),
  axis: z.number().int().min(0).max(7),
  gstat: z.number().int().min(0).max(0xffff_ffff),
  drv_status: z.number().int().min(0).max(0xffff_ffff),
});

export type ParsedEventPayloadMotorFault = z.infer<typeof EventPayloadMotorFaultSchema>;

// =====================================================================
// Plan 9 — DiscoveryReport (returned by GET /discovery on board/Pi/cloud-api)
// Spec: docs/superpowers/specs/2026-05-03-discovery-reconciliation-design.md §4.2
// =====================================================================

export const ChannelStateSchema = z.object({
  pin: z.number().int(),
  channel_type: z.string(),
  current_value: z.unknown().nullable(),
  current_value_ts_ms: z.number().int(),
  detected_via: z.enum(['auto_announce', 'probe', 'manual', 'inferred']),
  comm_errors_since_boot: z.number().int().nonnegative(),
});

export const DiscoveredBoardSchema = z.object({
  board_model_id: z.string().nullable(),
  serial: z.string(),
  bus_location: z.object({
    bus: z.string(),
    node_id: z.number().int(),
  }),
  detected_via: z.enum(['auto_announce', 'probe', 'manual']),
  detected_at_ms: z.number().int(),
  firmware_version: z.string().nullable(),
  channels: z.array(ChannelStateSchema),
  health: z.object({
    online: z.boolean(),
    last_seen_ms: z.number().int(),
    bus_error_rate: z.number().nonnegative(),
    voltage_v: z.number().nullable(),
    temp_c: z.number().nullable(),
  }),
});

export const BusDiagnosticsSchema = z.object({
  bus: z.string(),
  baud: z.number().int(),
  total_frames: z.number().int().nonnegative(),
  errored_frames: z.number().int().nonnegative(),
  retransmits: z.number().int().nonnegative(),
  bus_off_count: z.number().int().nonnegative(),
  link_quality: z.number().nullable(),
});

export const DiscoveryReportSchema = z.object({
  source: z.object({
    type: z.enum(['board', 'pi', 'cloud_api_stub']),
    id: z.string(),
    firmware_version: z.string(),
  }),
  generated_at_ms: z.number().int(),
  boards: z.array(DiscoveredBoardSchema),
  buses: z.array(BusDiagnosticsSchema),
});

export type ParsedChannelState = z.infer<typeof ChannelStateSchema>;
export type ParsedDiscoveredBoard = z.infer<typeof DiscoveredBoardSchema>;
export type ParsedBusDiagnostics = z.infer<typeof BusDiagnosticsSchema>;
export type ParsedDiscoveryReport = z.infer<typeof DiscoveryReportSchema>;
